import { Button, Input, Select, Divider } from '@scuf/common';
import useFetch from 'helpers/hooks/useFetch';
import { UserDTO, UserRegiao, UserRole } from 'models/user/sso';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_MANAGER, telephoneMask, COMPANY_MANAGER, notificationAtom } from 'resources';
import { CompanyDTO } from '../../../../../../models/userManager/addCustomer';
import { useRecoilState, useSetRecoilState } from 'recoil';

type Props = {
    user: UserDTO;
};

const InitialData: CompanyDTO = {
    id: 0,
    cnpj: '',
    idSap: '',
    name: '',
    code: '',
    registerNumber: '',
    street: '',
    email: '',
    number: '',
    complement: '',
    city: '',
    neighborhood: '',
    state: '',
    country: '',
    zipCode: '',
    phone: '',
    users: [],
    companyModules: [],
    companyLOB: []
};

const FinaluserInfo: React.FC<Props> = ({ user }: Props) => {
    const { t } = useTranslation(['usermanager', 'common', 'user']);
    const [changed, setChanged] = useState<number>();
    const [changedRegiao, setChangedRegiao] = useState<number>();
    const [disable, setDisable] = useState<boolean>(false);
    const [data, setData] = useState<CompanyDTO>(InitialData);
    const [cnpj, setCnpj] = useState<string>('');
    const [idSap, setIdSap] = useState<string>('');
    const [atualiza, setAtualiaza] = useState<boolean>(false);
    const setNotification = useSetRecoilState(notificationAtom);

    const [, update, loading] = useFetch<{ ssoId: string; roleId: number }>({
        url: USER_MANAGER.updateRole,
        method: 'GET',
        onError: 'Update',
        onSuccess: 'Update'
    });

    const [, getCompanyById, loadingById] = useFetch<{ id: number }>({
        url: COMPANY_MANAGER.getCompanyById,
        method: 'GET',
        onError: 'Update',
        onSuccess: 'Update'
    });

    const [, getCompanyByCnpj, loadingByCnpj] = useFetch<{ companyDto: string }>({
        url: COMPANY_MANAGER.getCompanyByCnpj,
        method: 'GET',
        onError: 'Update'
    });

    const [, getCompanyByIdSap, loadingByIdSap] = useFetch<{ idSap: string }>({
        url: COMPANY_MANAGER.GetCompanyByIdSap,
        method: 'GET',
        onError: 'Update'
    });

    const [, updateCompanyUser, loadingUpdateCompanyUser] = useFetch<{ ssoId: string; idCompany: number }>({
        url: USER_MANAGER.updateCompanyUser,
        method: 'GET',
        onError: 'Update',
        onSuccess: 'Update'
    });

    useEffect(() => {
        if (changed != null) {
            setDisable(false);
        } else {
            setDisable(true);
        }
    }, [changed]);

    useEffect(() => {
        if (changedRegiao == 1) {
            setIdSap('');
        } else if (changedRegiao == 2) {
            setCnpj('');
        }
        setAtualiaza(false);
    }, [changedRegiao]);

    const handleEdit = async (ssoId: string, roleId: number) => {
        await update({ ssoId, roleId });
        setDisable(true);
    };

    const atulizarDados = async (companyDto: string, idSap: string) => {
        if (cnpj) {
            await getCompanyByCnpj({ companyDto }, async resp => {
                if (resp.data) {
                    setData(prevData => ({
                        ...prevData,
                        name: resp.data.name,
                        cnpj: resp.data.cnpj,
                        id: resp.data.id,
                        email: resp.data.email,
                        city: resp.data.city,
                        neighborhood: resp.data.neighborhood,
                        country: resp.data.country,
                        phone: resp.data.phone,
                        state: resp.data.state,
                        street: resp.data.street,
                        zipCode: resp.data.zipCode,
                        companyModules: resp.data.companyModules || [],
                        companyPermission: resp.data.companyPermission || [],
                        idSap: resp.data.idSap
                    }));
                    setAtualiaza(true);
                    setNotification((old: any) => ({ ...old, message: 'user:info.UpdateData', type: 'success' }));
                } else {
                    setAtualiaza(false);
                    setNotification((old: any) => ({ ...old, message: 'user:info.IsNotCNPJ', type: 'critical' }));
                }
            });
        }

        if (idSap) {
            await getCompanyByIdSap({ idSap }, async resp => {
                if (resp.data) {
                    setData(prevData => ({
                        ...prevData,
                        name: resp.data.name,
                        cnpj: resp.data.cnpj,
                        id: resp.data.id,
                        email: resp.data.email,
                        city: resp.data.city,
                        neighborhood: resp.data.neighborhood,
                        country: resp.data.country,
                        phone: resp.data.phone,
                        state: resp.data.state,
                        street: resp.data.street,
                        zipCode: resp.data.zipCode,
                        companyModules: resp.data.companyModules || [],
                        companyPermission: resp.data.companyPermission || [],
                        idSap: resp.data.idSap
                    }));
                    setAtualiaza(true);
                    setNotification((old: any) => ({ ...old, message: 'user:info.UpdateData', type: 'success' }));
                } else {
                    setAtualiaza(false);
                    setNotification(old => ({ ...old, message: t('user:info.IsNotIdSap'), type: 'critical' }));
                }
            });
        }
    };

    const handleMigrar = async (ssoId: string, idCompany: number) => {
        await updateCompanyUser({ ssoId, idCompany }, async resp => {
            if (resp) {
                setAtualiaza(false);
                setNotification((old: any) => ({ ...old, message: 'user:info.MigratedSucess', type: 'success' }));
            } else {
                setNotification((old: any) => ({ ...old, message: 'user:info.MigratedError', type: 'critical' }));
            }
        });
        setAtualiaza(false);
    };

    const displayValue = (field: keyof CompanyDTO): string => {
        const value = data[field] && data[field] !== '' ? data[field] : user?.company?.[field];
        return value !== undefined && value !== null ? String(value) : '';
    };
    return (
        <>
            {user && (
                <>
                    <div className="form4Columns">
                        <Input label={t('user:info.Name')} value={user?.name} disabled></Input>
                        <Input label={t('Email')} value={user?.email} disabled></Input>
                        <Select
                            className="SelectStyle"
                            label={t('user:info.Role')}
                            options={[
                                { text: 'Super Admin', value: parseInt(UserRole.SUPERADMIN) },
                                { text: 'Admin', value: parseInt(UserRole.ADMIN) },
                                { text: 'Client Admin', value: parseInt(UserRole.CLIENTADMIN) },
                                { text: 'Client', value: parseInt(UserRole.CLIENT) }
                            ]}
                            defaultValue={user.roleId}
                            placeholder={t('user:info.Role')}
                            onChange={value => setChanged(value)}
                        ></Select>
                        <Input label={t('user:company.Name')} disabled value={displayValue('name')}></Input>
                        <Input label={t('user:company.Id')} value={displayValue('cnpj')} disabled></Input>
                        <Input label={t('user:address.Phone')} disabled value={telephoneMask(displayValue('phone'))}></Input>
                        <Input label={t('user:address.City')} disabled value={displayValue('city')}></Input>
                        <Input label={t('user:address.Street')} value={displayValue('street')} disabled></Input>
                        <Input label={t('user:address.State')} value={displayValue('state')} disabled></Input>
                        <Input label={t('user:address.Neighborhood')} value={displayValue('neighborhood')} disabled></Input>
                        <Input label={t('CEP')} disabled value={displayValue('zipCode')}></Input>
                        <Input label={t('user:address.Complement')} disabled value={displayValue('complement')}></Input>
                    </div>
                </>
            )}
            <div className="buttonsSearch">
                <Button
                    onClick={() => handleEdit(user.ssoId.toString(), changed!)}
                    content={t('common:actions.Change')}
                    loading={loading}
                    disabled={!changed || disable}
                ></Button>
            </div>
            <Divider direction="horizontal"> {t('user:actions.MigrateUser')}</Divider>
            <div className="form4Columns">
                <Select
                    className="SelectStyle"
                    label={t('user:address.Region')}
                    options={[
                        { text: 'Mexico', value: UserRegiao.MEXICO },
                        { text: 'Brasil', value: UserRegiao.BRASIL }
                    ]}
                    placeholder={t('user:address.Country')}
                    onChange={value => setChangedRegiao(value)}
                ></Select>
                {changedRegiao == 1 && <Input label={t('user:company.Id')} onChange={value => setCnpj(value)}></Input>}
                {changedRegiao == 2 && <Input label={t('user:company.IdSap')} onChange={value => setIdSap(value)}></Input>}
                <div className="buttonsSearch">
                    <Button
                        onClick={() => atulizarDados(cnpj, idSap)}
                        content={t('user:actions.toUpdate')}
                        loading={loadingUpdateCompanyUser || loadingByCnpj || loadingByIdSap}
                        disabled={!changedRegiao || atualiza}
                    ></Button>
                    <Button
                        onClick={() => handleMigrar(user.ssoId, data.id!)}
                        content={t('user:actions.Migrate')}
                        loading={loadingUpdateCompanyUser}
                        disabled={!atualiza}
                    ></Button>
                </div>
            </div>
        </>
    );
};

export default FinaluserInfo;
